import React from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Space,
  Table,
  Tag,
  Typography,
  Popconfirm,
} from 'antd';
import {
  CheckCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { c502Api, PendingItem } from '../../services/c502Api';
import { useC502Approval } from './hooks/useC502Approval';
import { BatchAcceptanceBridge, pwc502f1AcceptanceManifest } from '../../features/acceptance';
import './C502Approval.css';

const { Title, Text } = Typography;

// ============================================================
// Helpers
// ============================================================

function renderApprovalStat(stat: string): React.ReactNode {
  if (!stat || stat === 'N' || stat === '') return <Tag color="default">未送審</Tag>;
  if (stat === 'P') return <Tag color="green">已核准</Tag>;
  return <Tag color="blue">送審中 (L{stat})</Tag>;
}

function formatNumber(value: number, decimals = 2): string {
  if (!value && value !== 0) return '—';
  return value.toLocaleString('zh-TW', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

// ============================================================
// Main Component
// ============================================================

const C502Approval: React.FC = () => {
  const {
    loading,
    approving,
    pendingItems,
    total,
    userLevel,
    selectedKeys,
    filter,
    setFilter,
    setSelectedKeys,
    fetchPending,
    batchApprove,
    lastApiResponse,
  } = useC502Approval();

  // Auto-load all pending items on mount
  React.useEffect(() => { fetchPending(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================================
  // Table columns
  // ============================================================
  const columns: ColumnsType<PendingItem> = [
    {
      title: <span data-contract-field="fc110_01">S/C No</span>,
      dataIndex: 'sc_no',
      key: 'sc_no',
      width: 100,
      render: (text: string) => <Text code>{text}</Text>,
    },
    {
      title: <span data-contract-field="fc110_11">S/C 日期</span>,
      dataIndex: 'sc_date',
      key: 'sc_date',
      width: 110,
      render: (date: string | null) =>
        date ? new Date(date).toLocaleDateString('zh-TW') : '—',
    },
    {
      title: <span data-contract-field="ui_customer_name">客戶名稱</span>,
      dataIndex: 'customer_name',
      key: 'customer_name',
      ellipsis: true,
    },
    {
      title: <span data-contract-field="fc110_24">幣別</span>,
      dataIndex: 'currency',
      key: 'currency',
      width: 70,
      align: 'center',
    },
    {
      title: <span data-contract-field="fc110_32">銷售金額</span>,
      dataIndex: 'sales_amount',
      key: 'sales_amount',
      width: 130,
      align: 'right',
      render: (v: number) => formatNumber(v),
    },
    {
      title: <span data-contract-field="ui_gross_profit">毛利</span>,
      dataIndex: 'gross_profit',
      key: 'gross_profit',
      width: 130,
      align: 'right',
      render: (v: number) => (
        <span style={{ color: v >= 0 ? '#389e0d' : '#cf1322' }}>
          {formatNumber(v)}
        </span>
      ),
    },
    {
      title: <span data-contract-field="ui_margin_pct">毛利率 %</span>,
      dataIndex: 'margin_pct',
      key: 'margin_pct',
      width: 100,
      align: 'right',
      render: (v: number) => (
        <span style={{ color: v >= 0 ? '#389e0d' : '#cf1322' }}>
          {formatNumber(v, 1)}%
        </span>
      ),
    },
    {
      title: <span data-contract-field="fc110_51">核准狀態</span>,
      dataIndex: 'approval_stat',
      key: 'approval_stat',
      width: 110,
      align: 'center',
      render: renderApprovalStat,
    },
    {
      title: <span data-contract-field="ui_can_approve">可核准</span>,
      dataIndex: 'can_approve',
      key: 'can_approve',
      width: 80,
      align: 'center',
      render: (v: boolean) =>
        v ? <Tag color="success">是</Tag> : <Tag color="default">否</Tag>,
    },
  ];

  // ============================================================
  // Row selection
  // ============================================================
  const rowSelection = {
    selectedRowKeys: selectedKeys,
    onChange: (keys: React.Key[]) => setSelectedKeys(keys as string[]),
    getCheckboxProps: (record: PendingItem) => ({
      disabled: !record.can_approve,
      title: record.can_approve ? '' : '此等級無法核准',
    }),
  };

  // ============================================================
  // Filter form
  // ============================================================
  const handleSearch = () => { fetchPending(); };

  const handleFilterChange = (field: 'scStart' | 'scEnd', value: string) => {
    setFilter({ ...filter, [field]: value });
  };

  // ============================================================
  // Render
  // ============================================================
  return (
    <div className="c502-container">
      <Title level={4} style={{ marginBottom: 16 }}>S/C 核准 (pwc502f1)</Title>

      {/* Filter Bar */}
      <Card size="small" style={{ marginBottom: 12 }}>
        <Form layout="inline" size="small">
          <Form.Item label={<span data-contract-field="query_sc_start">S/C 起始</span>}>
            <Input
              value={filter.scStart}
              onChange={(e) => handleFilterChange('scStart', e.target.value)}
              placeholder="例：C24001"
              style={{ width: 120 }}
              onPressEnter={handleSearch}
            />
          </Form.Item>
          <Form.Item label={<span data-contract-field="query_sc_end">S/C 結束</span>}>
            <Input
              value={filter.scEnd}
              onChange={(e) => handleFilterChange('scEnd', e.target.value)}
              placeholder="例：C24099"
              style={{ width: 120 }}
              onPressEnter={handleSearch}
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
              loading={loading}
              data-acceptance-action="PWC502-ACT-LIST-PENDING"
            >
              查詢
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* Summary & Actions */}
      <Card size="small" style={{ marginBottom: 12 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Text type="secondary">
                共 <strong>{total}</strong> 筆待核准
              </Text>
              {userLevel && (
                <Text type="secondary">
                  您的核准等級：<Tag color="blue">L{userLevel}</Tag>
                </Text>
              )}
              {selectedKeys.length > 0 && (
                <Text type="secondary">
                  已勾選 <strong>{selectedKeys.length}</strong> 筆
                </Text>
              )}
            </Space>
          </Col>
          <Col>
            <Popconfirm
              title={`確認核准 ${selectedKeys.length} 筆 S/C？`}
              onConfirm={batchApprove}
              okText="確認核准"
              cancelText="取消"
              disabled={selectedKeys.length === 0}
            >
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                loading={approving}
                disabled={selectedKeys.length === 0}
                data-acceptance-action="PWC502-ACT-APPROVE"
              >
                批量核准
              </Button>
            </Popconfirm>
          </Col>
        </Row>
      </Card>

      {/* Data Table */}
      <Table<PendingItem>
        rowKey="sc_no"
        columns={columns}
        dataSource={pendingItems}
        loading={loading}
        rowSelection={rowSelection}
        size="small"
        pagination={{
          pageSize: 50,
          showSizeChanger: false,
          showTotal: (t) => `共 ${t} 筆`,
        }}
        scroll={{ x: 900 }}
        locale={{ emptyText: '無待核准資料' }}
      />
      <BatchAcceptanceBridge
        manifest={pwc502f1AcceptanceManifest}
        records={pendingItems}
        selectedKeys={selectedKeys}
        keyOf={(item) => item.sc_no}
        queryExact={async (key) => {
          const result = await c502Api.getPending({ scStart: key, scEnd: key });
          return result.items[0] || null;
        }}
        lastApiResponse={lastApiResponse}
        loading={loading || approving}
        onAutoFill={() => {
          const first = pendingItems.find(item => item.can_approve);
          if (first) setSelectedKeys([first.sc_no]);
        }}
        autoFillDisabled={!pendingItems.some(item => item.can_approve)}
        autoFillReason="安全勾選第一張可核准 S/C，不會自動執行核准。"
        screenDraft={{ filter, selected_items: selectedKeys }}
      />
    </div>
  );
};

export default C502Approval;
